import React, { Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import NetworkError from '../../components/Error';
import Loader from '../../components/Loader';
import Table, { SortableTh } from '../../components/Table';
import useSort from '../../components/Table/useSort';
import Pagination from '../../components/Pagination';
import usePagination from '../../components/Pagination/usePagination';
import { getWatcherData, getWatcherDataInitials } from '../../redux/actions/watcherActions';

import WatcherDataStyles from './WatcherData.module.css';

const FRONTEND_WATCHER_DATA_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	NO_WATCHERS_AVAILABLE: ErrorCodes.RESOURCE_NOT_FOUND,
	WATCHER_DATA_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	CSV_EXPORT_FAILED: ErrorCodes.FILE_PROCESSING_FAILED,
	PDF_EXPORT_FAILED: ErrorCodes.FILE_PROCESSING_FAILED,
};

const buildWatcherDataSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const WatcherDataScreen = () => {
	const dispatch = useDispatch();

	const [ selectedWatcher, setSelectedWatcher ] = React.useState('');
	const [ dataLoading, setDataLoading ] = React.useState(false);
	const [ watcherDataInitials, setWatcherDataInitials ] = React.useState({});
	const [ watcherData, setWatcherData ] = React.useState({});
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);

	const setWatcherDataSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildWatcherDataSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearWatcherDataSupportError = React.useCallback((force = false) => {
		if (!force) return;
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [ supportErrorMessage ]);

	React.useEffect(
		() => {
			const getData = async () => {
				setDataLoading(true);
				try {
					const data = await dispatch(getWatcherDataInitials());
					if (data && data.status) {
						setWatcherDataInitials(data);
						const hasWatchers =
							Array.isArray(data.watchers) &&
							data.watchers.length > 0 &&
							Array.isArray(data.watchers[0]) &&
							data.watchers[0].length >= 2;

						if (!hasWatchers) {
							setWatcherDataSupportError(
								FRONTEND_WATCHER_DATA_ERROR_CODES.NO_WATCHERS_AVAILABLE,
								'Watcher Data Page / No Watchers Available',
								'No watchers are configured for watcher data'
							);
						}
					} else {
						setWatcherDataInitials({});
						setWatcherDataSupportError(
							FRONTEND_WATCHER_DATA_ERROR_CODES.INITIAL_LOAD_FAILED,
							'Watcher Data Page / Initial Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'Watcher data initials request did not return success')
						);
					}
				} catch (error) {
					setWatcherDataInitials({});
					setWatcherDataSupportError(
						FRONTEND_WATCHER_DATA_ERROR_CODES.INITIAL_LOAD_FAILED,
						'Watcher Data Page / Initial Data Request Error',
						error?.message || String(error)
					);
				} finally {
					setDataLoading(false);
				}
			};

			getData();
		},
		[ dispatch, setWatcherDataSupportError ]
	);

	// No auto-selection: the page renders with the dropdown populated but no
	// watcher chosen. Data loads only after the user picks one (the effect below
	// early-returns while selectedWatcher is empty).

	React.useEffect(
		() => {
			let cancelled = false;
			const getData = async () => {
				if (!selectedWatcher) return;
				setDataLoading(true);
				setWatcherData({});   // clear the previous watcher's data immediately on switch

				const formData = new FormData();
				formData.append('watchers', selectedWatcher);

				try {
					const data = await dispatch(getWatcherData(formData));
					if (cancelled) return;   // superseded by a newer selection
					if (data && data.status) {
						setWatcherData(data);
					} else {
						setWatcherData({});
						setWatcherDataSupportError(
							FRONTEND_WATCHER_DATA_ERROR_CODES.WATCHER_DATA_LOAD_FAILED,
							'Watcher Data Page / Watcher Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'Watcher data request did not return success')
						);
					}
				} catch (error) {
					setWatcherData({});
					setWatcherDataSupportError(
						FRONTEND_WATCHER_DATA_ERROR_CODES.WATCHER_DATA_LOAD_FAILED,
						'Watcher Data Page / Watcher Data Request Error',
						error?.message || String(error)
					);
				} finally {
					setDataLoading(false);
				}
			};

			getData();
			return () => { cancelled = true; };
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[ dispatch, selectedWatcher ]
	);

	// Add this function to get the current watcher name
	const getCurrentWatcherName = () => {
		if (!selectedWatcher) return "Watcher";
		
		if (watcherDataInitials && watcherDataInitials.watchers && watcherDataInitials.watchers.length > 0) {
			const currentWatcher = watcherDataInitials.watchers.find(watcher => watcher[0] === selectedWatcher);
			return currentWatcher ? currentWatcher[1] : "Watcher";
		}
		
		return "Watcher";
	};

	// Add this function to generate dynamic filenames
	const generateFilename = (dataType) => {
		const watcherName = getCurrentWatcherName();
		return `${watcherName} ${dataType}`;
	};

	const exportToCSV = (tableData, columns, filename) => {
		if (!tableData || tableData.length === 0) {
			toast.error('No data to export');
			return;
		}

		try {
			// Create worksheet with headers
			const worksheet = XLSX.utils.aoa_to_sheet([columns]);

			// Add data rows
			XLSX.utils.sheet_add_aoa(worksheet, tableData, { origin: 'A2' });

			// Create workbook and add the worksheet
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

			// Generate CSV and download
			XLSX.writeFile(workbook, `${filename}.csv`);

			toast.success(`Exported to ${filename}.csv`);
		} catch (error) {
			const sourceMessage = error?.message || String(error);
			const formattedError = buildWatcherDataSupportError(
				FRONTEND_WATCHER_DATA_ERROR_CODES.CSV_EXPORT_FAILED,
				'Watcher Data Page / CSV Export Failed',
				sourceMessage
			);
			toast.error(formattedError);
			setWatcherDataSupportError(
				FRONTEND_WATCHER_DATA_ERROR_CODES.CSV_EXPORT_FAILED,
				'Watcher Data Page / CSV Export Failed',
				sourceMessage
			);
		}
	};

	const exportToPDF = (tableData, columns, filename) => {
		if (!tableData || tableData.length === 0) {
			toast.error('No data to export');
			return;
		}

		try {
			console.log('PDF Export - Column names:', columns);
			
			const doc = new jsPDF('landscape'); // Use landscape for better column fit
			
			// Add title
			doc.setFontSize(16);
			doc.text(filename, 14, 15);
			
			// Include ALL columns
			const exportColumns = [...columns];
			const exportData = [...tableData];
			
			// Manually create a table with improved spacing
			const startY = 25;
			const cellPadding = 1; // Reduced padding
			const fontSize = 7;
			const baseRowHeight = 10; // Reduced row height
			const lineHeight = 6; // Reduced line height
			
			// Set font size for the table
			doc.setFontSize(fontSize);
			
			// Calculate column width (distribute more intelligently)
			const pageWidth = doc.internal.pageSize.width;
			const margin = 10;
			const tableWidth = pageWidth - 2 * margin;
			
			// Check if any column might contain long text (like URLs)
			const hasUrlColumn = columns.some(col => col.toLowerCase().includes('url') || col.toLowerCase().includes('domain'));
			
			let colWidths = [];
			if (hasUrlColumn) {
				// Give more space to URL/domain columns
				const urlColumnWidth = tableWidth * 0.6; // 60% for URLs
				const remainingWidth = tableWidth - urlColumnWidth;
				const regularColumnWidth = remainingWidth / (exportColumns.length - 1);
				
				colWidths = exportColumns.map((col, index) => 
					col.toLowerCase().includes('url') || col.toLowerCase().includes('domain') ? 
					urlColumnWidth : regularColumnWidth
				);
			} else {
				// Equal distribution for other tables
				const colWidth = tableWidth / exportColumns.length;
				colWidths = new Array(exportColumns.length).fill(colWidth);
			}
			
			// Helper function to split text into lines that fit within the given width
			const splitTextToLines = (text, maxWidth) => {
				const words = text.split(' ');
				let lines = [];
				let currentLine = '';
				
				words.forEach(word => {
					const testLine = currentLine + (currentLine ? ' ' : '') + word;
					if (doc.getTextWidth(testLine) <= maxWidth) {
						currentLine = testLine;
					} else {
						if (currentLine) lines.push(currentLine);
						currentLine = word;
					}
				});
				if (currentLine) lines.push(currentLine);
				
				return lines;
			};
			
			// Draw header
			doc.setFillColor(66, 139, 202); // Header background color
			doc.setTextColor(255, 255, 255); // Header text color
			doc.setFont(undefined, 'bold');
			
			doc.rect(margin, startY, tableWidth, baseRowHeight, 'F');
			
			let currentX = margin;
			exportColumns.forEach((column, i) => {
				doc.text(column.toString(), currentX + cellPadding, startY + baseRowHeight - cellPadding);
				currentX += colWidths[i];
			});
			
			// Draw rows
			doc.setTextColor(0, 0, 0); // Reset text color
			doc.setFont(undefined, 'normal');
			
			let y = startY + baseRowHeight;
			
			// Draw cells for each row
			exportData.forEach((row, rowIndex) => {
				// First pass: calculate the maximum number of lines needed for this row
				let maxLinesInRow = 1;
				const rowTextLines = []; // Store the lines for each cell
				
				row.forEach((cell, colIndex) => {
					if (cell !== null && cell !== undefined) {
						const text = cell.toString();
						const maxWidth = colWidths[colIndex] - (2 * cellPadding);
						const columnName = exportColumns[colIndex];
						
						// Check if this column might contain long text
						const isLongTextColumn = columnName.toLowerCase().includes('url') || 
												columnName.toLowerCase().includes('domain') ||
												text.length > 50;
						
						if (isLongTextColumn && text.length > 0) {
							const lines = splitTextToLines(text, maxWidth);
							const maxLines = 3; // Allow up to 3 lines for long text
							const finalLines = lines.length > maxLines ? 
								[...lines.slice(0, maxLines - 1), lines.slice(maxLines - 1).join(' ') + '...'] : 
								lines;
							
							rowTextLines[colIndex] = finalLines;
							maxLinesInRow = Math.max(maxLinesInRow, finalLines.length);
						} else {
							// For regular columns, check if text needs truncation
							if (doc.getTextWidth(text) <= maxWidth) {
								rowTextLines[colIndex] = [text];
							} else {
								let truncated = text;
								while (doc.getTextWidth(truncated + "...") > maxWidth && truncated.length > 0) {
									truncated = truncated.substring(0, truncated.length - 1);
								}
								rowTextLines[colIndex] = [truncated + "..."];
							}
						}
					} else {
						rowTextLines[colIndex] = [''];
					}
				});
				
				// Calculate row height based on maximum lines
				const rowHeight = Math.max(baseRowHeight, maxLinesInRow * lineHeight + (cellPadding * 2));
				
				// Set background color for alternating rows
				if (rowIndex % 2 === 0) {
					doc.setFillColor(240, 240, 240);
				} else {
					doc.setFillColor(255, 255, 255);
				}
				
				doc.rect(margin, y, tableWidth, rowHeight, 'F');
				
				// Second pass: draw the text lines
				currentX = margin;
				row.forEach((cell, colIndex) => {
					const lines = rowTextLines[colIndex];
					
					// Draw each line of text for this cell
					lines.forEach((line, lineIndex) => {
						const textY = y + cellPadding + (lineIndex + 1) * lineHeight - 1;
						doc.text(
							line,
							currentX + cellPadding,
							textY
						);
					});
					
					currentX += colWidths[colIndex];
				});
				
				y += rowHeight;
				
				// Add new page if needed
				if (y > doc.internal.pageSize.height - 20) {
					doc.addPage();
					y = 25;
				}
			});
			
			// Save PDF
			doc.save(`${filename}.pdf`);
			
			toast.success(`Exported to ${filename}.pdf`);
		} catch (error) {
			console.error('PDF generation error:', error);
			const sourceMessage = error?.message || String(error);
			const formattedError = buildWatcherDataSupportError(
				FRONTEND_WATCHER_DATA_ERROR_CODES.PDF_EXPORT_FAILED,
				'Watcher Data Page / PDF Export Failed',
				sourceMessage
			);
			toast.error(formattedError);
			setWatcherDataSupportError(
				FRONTEND_WATCHER_DATA_ERROR_CODES.PDF_EXPORT_FAILED,
				'Watcher Data Page / PDF Export Failed',
				sourceMessage
			);
		}
	};

	// Client-side column sort for each table. Rows are arrays, so the sort field
	// is the column index. Each table gets its own useSort instance; SortableTh
	// reads sortKey/sortDir/requestSort straight off the returned object.
	const ctrSort = useSort(watcherData.CTRRows || []);
	const ipsSort = useSort(watcherData.IPsRows || []);
	const portsSort = useSort(watcherData.PortsRows || []);
	const dnsSort = useSort(watcherData.DNSRows || []);
	const urlSort = useSort(watcherData.UrlRows || []);

	const { pageRows, pageNumber, totalRows, pageLimit, setPageNumber, handlePageChange } = usePagination(urlSort.sorted);

	// The URLs table is paginated — snap back to page 1 when its sort changes.
	React.useEffect(() => {
		setPageNumber(1);
	}, [urlSort.sortKey, urlSort.sortDir, setPageNumber]);

	return (
		<Content
			currentMenu='watchers'
			headerTitle='Watcher Data'
			selectBox={true}
			selectBoxValue={selectedWatcher}
			selectOnchange={(e) => setSelectedWatcher(e.target.value)}
			options={
				<>
					<option value=''>Select a Watcher</option>
					{watcherDataInitials &&
					Object.keys(watcherDataInitials).length === 0 &&
					watcherDataInitials.watchers &&
					watcherDataInitials.watchers.length === 0 ? (
						<option value='' disabled>No watchers found</option>
					) : (
						watcherDataInitials &&
						Object.keys(watcherDataInitials).length > 0 &&
						watcherDataInitials.watchers &&
						watcherDataInitials.watchers.map(
							(watcher, key) =>
								watcher.length >= 2 && (
									<option value={watcher[0]} key={watcher + key}>
										{watcher[1]}
									</option>
								)
						)
					)}
				</>
			}>
			{dataLoading ? (
				<Loader />
			) : watcherData && Object.keys(watcherData).length === 0 ? (
				<div>
					{supportErrorMessage ? (
						<>
							<NetworkError />
							<div className={WatcherDataStyles.supportErrorCard}>
								<strong>Support Error:</strong>
								<code className={WatcherDataStyles.supportErrorCode}>{supportErrorMessage}</code>
								<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
									<button
										type='button'
										className={WatcherDataStyles.copySupportErrorButton}
										onClick={handleCopySupportError}
									>
										{copiedSupportError ? 'Copied' : 'Copy Error Details'}
									</button>
									<button
										type='button'
										className={WatcherDataStyles.copySupportErrorButton}
										onClick={() => clearWatcherDataSupportError(true)}
									>
										Clear
									</button>
								</div>
							</div>
						</>
					) : !selectedWatcher ? (
						<p style={{ textAlign: 'center', padding: '2rem' }}>Select a Watcher to view data.</p>
					) : (
						<Loader />
					)}
				</div>
			) : (
				<Fragment>
					{supportErrorMessage && (
						<div className={WatcherDataStyles.supportErrorBanner}>
							<div className={WatcherDataStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={WatcherDataStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={WatcherDataStyles.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={WatcherDataStyles.copySupportErrorButton}
									onClick={() => clearWatcherDataSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
					<div className={WatcherDataStyles.tables}>
						<div className={WatcherDataStyles.table}>
							<div className={WatcherDataStyles.table_header}>
								<h3 className={WatcherDataStyles.table_header_text}>Top 10 Destination Countries</h3>
								<div className={WatcherDataStyles.export_buttons}>
									<button
										className={WatcherDataStyles.export_button}
										onClick={() => exportToCSV(watcherData.CTRRows, watcherData.CTRCols, generateFilename('Top Countries'))}
									>
										Export CSV
									</button>
									<button
										className={WatcherDataStyles.export_button}
										onClick={() => exportToPDF(watcherData.CTRRows, watcherData.CTRCols, generateFilename('Top Countries'))}
									>
										Export PDF
									</button>
								</div>
							</div>
							<Table
								tableHead={
									<tr>
										{watcherData.CTRCols &&
											watcherData.CTRCols.length > 0 &&
											watcherData.CTRCols.map((col, index) => (
												<SortableTh key={col + index} label={col} field={index} sort={ctrSort} />
											))}
									</tr>
								}
								tableRow={
									watcherData && watcherData.CTRRows && watcherData.CTRRows.length > 0 ? (
										ctrSort.sorted.map((rows, index) => (
											<tr key={index}>{rows && rows.length > 0 && rows.map((row) => <td key={row}>{row}</td>)}</tr>
										))
									) : (
										<tr>
											<td colSpan={watcherData.CTRCols.length} style={{ textAlign: 'center' }}>
												No data found
											</td>
										</tr>
									)
								}
							/>
						</div>

						<div className={WatcherDataStyles.table}>
							<div className={WatcherDataStyles.table_header}>
								<h3 className={WatcherDataStyles.table_header_text}>Top 10 Destination IPs</h3>
								<div className={WatcherDataStyles.export_buttons}>
									<button
										className={WatcherDataStyles.export_button}
										onClick={() => exportToCSV(watcherData.IPsRows, watcherData.IPsCols, generateFilename('Top Ips'))}
									>
										Export CSV
									</button>
									<button
										className={WatcherDataStyles.export_button}
										onClick={() => exportToPDF(watcherData.IPsRows, watcherData.IPsCols, generateFilename('Top Ips'))}
									>
										Export PDF
									</button>
								</div>
							</div>
							<Table
								tableHead={
									<tr>
										{watcherData.IPsCols &&
											watcherData.IPsCols.length > 0 &&
											watcherData.IPsCols.map((col, index) => (
												<SortableTh key={col + index} label={col} field={index} sort={ipsSort} />
											))}
									</tr>
								}
								tableRow={
									watcherData && watcherData.IPsRows && watcherData.IPsRows.length > 0 ? (
										ipsSort.sorted.map((rows, index) => (
											<tr key={index}>{rows && rows.length > 0 && rows.map((row) => <td key={row}>{row}</td>)}</tr>
										))
									) : (
										<tr>
											<td colSpan={watcherData.IPsCols.length} style={{ textAlign: 'center' }}>
												No data found
											</td>
										</tr>
									)
								}
							/>
						</div>

						<div className={WatcherDataStyles.table}>
							<div className={WatcherDataStyles.table_header}>
								<h3 className={WatcherDataStyles.table_header_text}>Top 10 Destination Ports</h3>
								<div className={WatcherDataStyles.export_buttons}>
									<button
										className={WatcherDataStyles.export_button}
										onClick={() => exportToCSV(watcherData.PortsRows, watcherData.PortsCols, generateFilename('Top Ports'))}
									>
										Export CSV
									</button>
									<button
										className={WatcherDataStyles.export_button}
										onClick={() => exportToPDF(watcherData.PortsRows, watcherData.PortsCols, generateFilename('Top Ports'))}
									>
										Export PDF
									</button>
								</div>
							</div>
							<Table
								tableHead={
									<tr>
										{watcherData.PortsCols &&
											watcherData.PortsCols.length > 0 &&
											watcherData.PortsCols.map((col, index) => (
												<SortableTh key={col + index} label={col} field={index} sort={portsSort} />
											))}
									</tr>
								}
								tableRow={
									watcherData && watcherData.PortsRows && watcherData.PortsRows.length > 0 ? (
										portsSort.sorted.map((rows, index) => (
											<tr key={index}>{rows && rows.length > 0 && rows.map((row) => <td key={row}>{row}</td>)}</tr>
										))
									) : (
										<tr>
											<td colSpan={watcherData.PortsCols.length} style={{ textAlign: 'center' }}>
												No data found
											</td>
										</tr>
									)
								}
							/>
						</div>

						<div className={WatcherDataStyles.table}>
							<div className={WatcherDataStyles.table_header}>
								<h3 className={WatcherDataStyles.table_header_text}>Top 10 DNS Lookups</h3>
								<div className={WatcherDataStyles.export_buttons}>
									<button
										className={WatcherDataStyles.export_button}
										onClick={() => exportToCSV(watcherData.DNSRows, watcherData.DNSCols, generateFilename('Top Dns'))}
									>
										Export CSV
									</button>
									<button
										className={WatcherDataStyles.export_button}
										onClick={() => exportToPDF(watcherData.DNSRows, watcherData.DNSCols, generateFilename('Top Dns'))}
									>
										Export PDF
									</button>
								</div>
							</div>
							<Table
								tableHead={
									<tr>
										{watcherData.DNSCols &&
											watcherData.DNSCols.length > 0 &&
											watcherData.DNSCols.map((col, index) => (
												<SortableTh key={col + index} label={col} field={index} sort={dnsSort} />
											))}
									</tr>
								}
								tableRow={
									watcherData && watcherData.DNSRows && watcherData.DNSRows.length > 0 ? (
										dnsSort.sorted.map((rows, index) => (
											<tr key={index}>{rows && rows.length > 0 && rows.map((row) => <td key={row}>{row}</td>)}</tr>
										))
									) : (
										<tr>
											<td colSpan={watcherData.DNSCols.length} style={{ textAlign: 'center' }}>
												No data found
											</td>
										</tr>
									)
								}
							/>
						</div>
					</div>
					<div className={WatcherDataStyles.table} style={{ marginTop: '2rem' }}>
						<div className={WatcherDataStyles.table_header}>
							<h3 className={WatcherDataStyles.table_header_text}>Top 10 URLs</h3>
							<div className={WatcherDataStyles.export_buttons}>
								<button
									className={WatcherDataStyles.export_button}
									onClick={() => exportToCSV(watcherData.UrlRows, watcherData.UrlCols, generateFilename('Top Urls'))}
								>
									Export CSV
								</button>
								<button
									className={WatcherDataStyles.export_button}
									onClick={() => exportToPDF(watcherData.UrlRows, watcherData.UrlCols, generateFilename('Top Urls'))}
								>
									Export PDF
								</button>
							</div>
						</div>
						<Table
							containerStyle={{ minHeight: '40vh' }}
							tableHead={
								<tr>
									{watcherData.UrlCols &&
										watcherData.UrlCols.length > 0 &&
										watcherData.UrlCols.map((col, index) => (
											<SortableTh key={col + index} label={col} field={index} sort={urlSort} />
										))}
								</tr>
							}
							tableRow={
								watcherData && watcherData.UrlRows && pageRows.length > 0 ? (
									pageRows.map((rows, index) => (
										<tr key={index}>{rows && rows.length > 0 && rows.map((row) => <td key={row}>{row}</td>)}</tr>
									))
								) : (
									<tr>
										<td colSpan={watcherData.UrlCols.length} style={{ textAlign: 'center' }}>
											No data found
										</td>
									</tr>
								)
							}
						/>
						<div className='paginationContainer'>
							<Pagination
								onPageChange={handlePageChange}
								rowsPerPage={pageLimit}
								totalPageSize={totalRows}
								pageNumber={pageNumber}
							/>
						</div>
					</div>
				</Fragment>
			)}
		</Content>
	);
};

export default WatcherDataScreen;
